import React, { useState, useEffect } from 'react';
import { Collapse, List, Button, Typography, Space, Input, Form, Popconfirm, message } from 'antd';
import { DownOutlined, RightOutlined, LeftOutlined, PlusOutlined, EditOutlined, DeleteOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;
const { TextArea } = Input;

/**
 * InputEditableList component for displaying a list with editable items
 * 
 * @param {Object} props
 * @param {Object} props.data - The data object containing list items
 * @param {string} props.title - The title of the accordion
 * @param {string} props.uuid - The UUID of the data object
 * @param {number} props.currentVersion - Current version of the data
 * @param {Object} props.versionInfo - Information about all versions
 * @param {Function} props.onVersionChange - Callback when version is changed
 * @param {Function} props.registerComponent - Function to register for updates
 * @param {string} props.sessionId - The current session ID
 * @param {string} props.endpointUrl - The endpoint URL to submit edits to
 */
const InputEditableList = ({
  data,
  title = "Editable List Items",
  uuid,
  currentVersion = 1,
  versionInfo = {},
  onVersionChange,
  registerComponent,
  sessionId,
  endpointUrl,
  agentId,
  onProcessingStart,
  onProcessingEnd
}) => {
  // State to track whether the component is expanded
  const [isExpanded, setIsExpanded] = useState(true);
  
  // State to track the edited list items
  const [editedItems, setEditedItems] = useState([]);
  
  // State to track the item being edited
  const [editingIndex, setEditingIndex] = useState(null);
  
  // State to track loading state
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form for editing items
  const [form] = Form.useForm();
  

  // Initialize items when data changes
  useEffect(() => {
    if (data && data.list_items) {
      // Convert data format to editable format
      const items = data.list_items.map(item => {
        const [prefix, mainLine, subLine] = item;
        return {
          prefix,
          mainLine,
          subLine
        };
      });
      setEditedItems(items);
    }
  }, [data]);
  
  // Handler for version navigation
  const handleVersionChange = (newVersion) => {
    if (onVersionChange && uuid) {
      onVersionChange(uuid, newVersion);
    }
  };
  
  // Handle edit button click
  const handleEdit = (index) => {
    const item = editedItems[index];
    setEditingIndex(index);
    
    // Set form values
    form.setFieldsValue({
      mainLine: item.mainLine,
      subLine: item.subLine
    });
  };
  
  // Handle delete button click
  const handleDelete = (index) => {
    setEditedItems(prev => {
      const newItems = [...prev];
      newItems.splice(index, 1);
      return newItems;
    });
  };
  
  // Handle save edit
  const handleSaveEdit = () => {
    form.validateFields().then(values => {
      const { mainLine, subLine } = values;
      
      setEditedItems(prev => {
        const newItems = [...prev];
        newItems[editingIndex] = {
          ...newItems[editingIndex],
          mainLine,
          subLine
        };
        return newItems;
      });
      
      // Clear editing state
      setEditingIndex(null);
      form.resetFields();
    });
  };
  
  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingIndex(null);
    form.resetFields();
  };
  
  // Handle add new item
  const handleAddItem = () => {
    // Add a new blank item and start editing it
    setEditedItems(prev => {
      const newItems = [...prev, { prefix: 'bullet', mainLine: '', subLine: '' }];
      return newItems;
    });
    
    // Start editing the new item
    setEditingIndex(editedItems.length);
    form.setFieldsValue({ mainLine: '', subLine: '' });
  };
  
  // Handle submit all changes
  const handleSubmit = async () => {
    if (!sessionId || !endpointUrl) {
      message.error('Missing session ID or endpoint URL');
      return;
    }
    
    // Call progress start
    if (onProcessingStart) {
      onProcessingStart();
    }
    
    try {
      setIsSubmitting(true);
      
      // Convert back to the original format
      const formattedItems = editedItems.map(item => [
        item.prefix,
        item.mainLine,
        item.subLine
      ]);
      
      // Create updated data object
      const updatedData = {
        ...data,
        list_items: formattedItems
      };
      
      // Prepare the payload
      const payload = {
        sessionId,
        componentId: uuid,
        componentType: 'input_editable_list',
        data: updatedData
      };
      
      // Send the data to the backend
      const response = await fetch(endpointUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify(payload),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Submission failed with status ${response.status}`);
      }
      
      const result = await response.json();
      
      // Handle success
      message.success(result.message || 'Changes saved successfully');
      
      // If there are updated components, handle them
      if (result.updatedComponentIds && result.updatedComponentIds.length > 0) {
        console.log('Components updated:', result.updatedComponentIds);
      }
    } catch (error) {
      console.error('Error submitting changes:', error);
      message.error(`Failed to save changes: ${error.message}`);
    } finally {
      setIsSubmitting(false);
      if (onProcessingEnd) {
        onProcessingEnd();
      }       
    }
  };
  
  // Register this component for updates if the function is provided
  useEffect(() => {
    if (registerComponent && uuid) {
      // Create a refresh function to update this component
      const refreshComponent = () => {
        console.log(`Refreshing Component: ${uuid}`);
        // This will trigger a re-render
        setIsExpanded(prev => prev);
      };
      
      // Register this component with the provided function
      const unregister = registerComponent(refreshComponent);
      
      // Unregister when the component unmounts
      return unregister;
    }
  }, [registerComponent, uuid]);
  
  // Check if version navigation should be displayed
  const showVersionNavigation = versionInfo && 
                               versionInfo.available_versions && 
                               versionInfo.available_versions.length > 1;

                             

  return (
    <div style={{ 
      marginBottom: '16px', 
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
      borderRadius: '4px'
    }}>
      <Collapse
        defaultActiveKey={['1']}
        expandIcon={({ isActive }) => {
          setIsExpanded(isActive);
          return isActive ? <DownOutlined /> : <RightOutlined />;
        }}
        items={[
          {
            key: '1',
            label: (
              <div style={{ fontWeight: 'bold', fontSize: '18px'  }}>
                {data?.list_title || title}
              </div>
            ),
            children: (
              <div>
                {/* Optional Description */}
                {data?.description && (
                  <Text style={{ display: 'block', marginBottom: '16px' }}>
                    {data.description}
                  </Text>
                )}
                
                {/* Add New Item Button */}
                <div style={{ marginBottom: '16px' }}>
                  <Button 
                    type="dashed" 
                    icon={<PlusOutlined />} 
                    onClick={handleAddItem}
                    style={{ width: '100%' }}
                  >
                    Add Item
                  </Button>
                </div>
                
                {/* List Items with Edit/Delete Actions */}
                {editedItems.length > 0 ? (
                  <List
                    itemLayout="horizontal"
                    dataSource={editedItems}
                    renderItem={(item, index) => (
                      <List.Item 
                        key={index} 
                        style={{ padding: '8px 0' }}
                        actions={editingIndex === index ? [] : [
                          <Button 
                            icon={<EditOutlined />} 
                            size="small" 
                            onClick={() => handleEdit(index)} 
                          />,
                          <Popconfirm
                            title="Delete this item?"
                            onConfirm={() => handleDelete(index)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button 
                              icon={<DeleteOutlined />} 
                              size="small" 
                              danger 
                            />
                          </Popconfirm>
                        ]}
                      >
                        {editingIndex === index ? (
                          <Form
                            form={form}
                            layout="vertical"
                            style={{ width: '100%' }}
                          >
                            <Form.Item
                              name="mainLine"
                              rules={[{ required: true, message: 'Please enter the main text' }]}
                            >
                              <Input placeholder="Main text" />
                            </Form.Item>
                            <Form.Item
                              name="subLine"
                            >
                              <TextArea 
                                placeholder="Description (optional)" 
                                autoSize={{ minRows: 1, maxRows: 3 }} 
                              />
                            </Form.Item>
                            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                              <Space>
                                <Button 
                                  icon={<CloseOutlined />} 
                                  onClick={handleCancelEdit}
                                >
                                  Cancel
                                </Button>
                                <Button 
                                  type="primary" 
                                  icon={<SaveOutlined />} 
                                  onClick={handleSaveEdit}
                                  style={{
                                    backgroundColor: '#6200EE',
                                    borderColor: '#6200EE'
                                  }}
                                >
                                  Save
                                </Button>
                              </Space>
                            </Form.Item>
                          </Form>
                        ) : (
                          <div>
                            <div style={{ fontWeight: 'bold' }}>{item.mainLine}</div>
                            {item.subLine && <div style={{ color: '#666' }}>{item.subLine}</div>}
                          </div>
                        )}
                      </List.Item>
                    )}
                  />
                ) : (
                  <Text>No list items yet. Click "Add Item" to begin.</Text>
                )}
                
                {/* Submit Button */}
                {editedItems.length > 0 && editingIndex === null && (
                  <div style={{ marginTop: '16px', textAlign: 'right' }}>
                    <Button
                      type="primary"
                      loading={isSubmitting}
                      onClick={handleSubmit}
                      style={{
                        backgroundColor: '#6200EE',
                        borderColor: '#6200EE'
                      }}
                    >
                      Save All Changes
                    </Button>
                  </div>
                )}
                
                {/* Version navigation */}
                {showVersionNavigation && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '16px', 
                    paddingTop: '16px',
                    borderTop: '1px solid #f0f0f0'
                  }}>
                    <Button 
                      size="small" 
                      icon={<LeftOutlined />}
                      disabled={currentVersion <= Math.min(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex > 0) {
                          handleVersionChange(versions[currentIndex - 1]);
                        }
                      }}
                    >
                      Previous
                    </Button>
                    
                    <div>
                      Version {currentVersion} of {versionInfo.total_versions}
                    </div>
                    
                    <Button 
                      size="small" 
                      icon={<RightOutlined />}
                      disabled={currentVersion >= Math.max(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex < versions.length - 1) {
                          handleVersionChange(versions[currentIndex + 1]);
                        }
                      }}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            )
          }
        ]}
      />
    </div>
  );
};

export default InputEditableList;